import React, { useState } from "react";
import { Button } from "@clockwise/design-system";
import { TextField, CircularProgress, TextFieldProps } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { colors, greys, fontFamilyIdealSans, fontFamilyHelvetica } from "@styles/index";

import { kebabCase } from "lodash";
import { Close } from "@material-ui/icons";
import { narrowMediaQuery } from "@ui/scheduling-link/scheduling-link.styles";
import { ModalDialog } from "./ModalDialog";
import { EventSummary } from "@ui/scheduling-confirmation/event-summary";
import { ConfirmationType } from "@ui/scheduling-confirmation";
import { isValidEmailFormat } from "@clockwise/client-commons/src/util/email";
import { Notification } from "@ui/Notification";
import { asMinutes } from "@util/date.util";
import { Booking } from "@ui/scheduling-link/CurrentBookingDetails";

import {
  orgPrimaryEmail,
  schedulingLinkBookedName,
  schedulingLinkBookedEmail,
} from "@util/local-storage";
import classNames from "classnames";
import { useRouter } from "found";

export type SlotTimeRange = {
  startTime: {
    dateOrDateTime: string;
    millis: number;
    timeZone: string | null;
  };
  endTime: {
    dateOrDateTime: string;
    millis: number;
    timeZone: string | null;
  };
};

type ReschedulePayload = {
  action: "reschedule";
  reason: string;
};

type BookPayload = {
  action: "book";
  attendeeName: string;
  attendeeEmailAddress: string;
};

export type ConfirmPayload = BookPayload | ReschedulePayload;

export type BookingModalProps = {
  meetingSettingsId: string;
  title: string;
  duration: number;
  existingBooking?: Booking | null;
  slotTimeRange?: SlotTimeRange;
  onConfirm: (payload: ConfirmPayload, selectedTime: SlotTimeRange) => void;
  onClose: () => void;
  loading: boolean;
  errorMsg?: string;
  timeTaken: boolean;
  fullscreen: boolean;
};

type FormInputProps = TextFieldProps & { label: string; showErrors?: boolean; "cw-id"?: string };

export const useStyles = makeStyles({
  header: {
    fontFamily: fontFamilyIdealSans,
    fontSize: "1.3rem",
    fontWeight: "bold",
    margin: 0,
    color: colors.blueDusk.darker,

    [narrowMediaQuery]: {
      margin: "auto",
    },
  },
  headerAndClose: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 7,

    [narrowMediaQuery]: {
      flexDirection: "row-reverse",
      justifyContent: "center",
    },
  },
  close: {
    marginRight: -5,

    [narrowMediaQuery]: {
      marginLeft: -10,
      marginRight: -24,
    },
  },
  todayIcon: {
    paddingRight: 8,
    paddingLeft: 5,
    fontSize: 20,
    width: "auto",
  },
  title: {
    fontWeight: "bold",
    fontSize: "1rem",
    marginBottom: 5,
    marginTop: 2,
    fontFamily: fontFamilyHelvetica,
    color: colors.blueDusk.darker,
  },
  dateTime: {
    fontSize: "1rem",
  },
  subheaders: {
    color: greys.standard,
    fontSize: ".9375rem",
    fontFamily: fontFamilyHelvetica,
    marginTop: 15,
    marginBottom: 10,

    "&.red": {
      color: colors.red.bright,
    },
  },
  textBox: {
    backgroundColor: greys.paleGrey,
    fontSize: "1.0rem",
    marginRight: 18,
    borderRadius: 4,

    "& input": {
      color: greys.darkestGrey,
    },

    "& placeholder": {
      color: greys.blueGrey,
    },

    "& .MuiInput-formControl": {
      padding: "6px 6px 3px 14px",
    },

    "& .Mui-focused": {
      borderBottom: `2px solid ${colors.green.darker}`,
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      marginBottom: -2, // Top offset the height added by the border to prevent content jumping down.
    },
  },
  bookItContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "23px 0px 3px 0px",
    alignItems: "center",

    [narrowMediaQuery]: {
      bottom: 23,
      right: 23,
      position: "absolute",
    },
  },
  errorMsg: {
    color: colors.red.bright,
    fontSize: `${15 / 16}rem`,
    paddingRight: 16,
    fontFamily: fontFamilyHelvetica,
  },
  loadingSpinner: {
    marginRight: 16,
  },
});

function FormInput({
  "cw-id": cwId,
  label,
  showErrors = false,
  error = false,
  ...props
}: FormInputProps) {
  const classes = useStyles();
  const labelId = `${kebabCase(label)}-label`;
  return (
    <>
      <div id={labelId} className={classNames(classes.subheaders, { red: error && showErrors })}>
        {label}
      </div>
      <TextField
        InputProps={{
          disableUnderline: true,
        }}
        inputProps={{
          "aria-labelledby": labelId,
        }}
        className={classes.textBox}
        spellCheck={false}
        fullWidth
        autoFocus
        cw-id={cwId}
        {...props}
      />
    </>
  );
}

const getUserEmail = (paramEmail: string): string => {
  // Deliberately use ?? and then || here. If a user sees their orgPrimaryEmail populated
  // but then they clear it, don't recommend it again. This works because by deleting it from the textfield,
  // it gets set to "" in local storage, and with this ?? it will still get rendered as "" instead of popping
  // over to orgPrimaryEmail.
  return schedulingLinkBookedEmail.get() ?? (paramEmail || orgPrimaryEmail.get() || "");
};

export const BookingModal = ({
  title,
  duration,
  existingBooking,
  slotTimeRange,
  onConfirm,
  onClose,
  loading,
  errorMsg,
  fullscreen,
  timeTaken = false,
}: BookingModalProps) => {
  const classes = useStyles();
  const {
    match: {
      location: {
        query: { email: paramEmail, name: paramName },
      },
    },
  } = useRouter();

  const [fullName, setFullName] = useState(schedulingLinkBookedName.get() || paramName || "");
  const [email, setEmail] = useState(getUserEmail(paramEmail));
  const [reason, setReason] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  /* Should never be null and needs a webserver change to make NonNull, but if so, local tz which should be correct vast majority of the time */
  const timeZone = slotTimeRange?.startTime.timeZone || "local";

  const onConfirmBooking = () => {
    if (!fullName || !isValidEmailFormat(email)) {
      setShowErrors(true);
    } else {
      if (slotTimeRange) {
        onConfirm(
          {
            action: "book",
            attendeeName: fullName.trim(),
            attendeeEmailAddress: email,
          },
          slotTimeRange,
        );
      }
    }
  };

  const onConfirmReschedule = () => {
    if (slotTimeRange) {
      onConfirm({ action: "reschedule", reason }, slotTimeRange);
    }
  };

  const onChangeReason = (e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value);
  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fullName = e.target.value;
    if (fullName) {
      schedulingLinkBookedName.set(fullName);
    } else {
      schedulingLinkBookedName.remove();
    }

    setShowErrors(false);
    setFullName(fullName);
  };
  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    if (email) {
      schedulingLinkBookedEmail.set(email);
    } else {
      schedulingLinkBookedEmail.remove();
    }

    setShowErrors(false);
    setEmail(email);
  };

  const bookForm = (
    <>
      <div>
        <FormInput
          label="Your full name"
          showErrors={showErrors}
          error={!fullName}
          placeholder="Enter your name"
          value={fullName}
          onChange={onChangeName}
          cw-id="booking-modal-name-field"
        />
        <FormInput
          label="Your email"
          showErrors={showErrors}
          error={!email}
          placeholder="Enter your email"
          value={email}
          onChange={onChangeEmail}
          cw-id="booking-modal-email-field"
        />
      </div>
      <div className={classes.bookItContainer}>
        {errorMsg && (
          <span className={classes.errorMsg} cw-id="booking-error-message">
            {errorMsg}
          </span>
        )}
        {!errorMsg && showErrors && !isValidEmailFormat(email) && (
          <span className={classes.errorMsg}>Invalid email address</span>
        )}
        {loading && (
          <CircularProgress size={18} color="primary" className={classes.loadingSpinner} />
        )}
        <Button
          onClick={onConfirmBooking}
          sentiment="positive"
          disabled={loading}
          size="large"
          aria-label={timeTaken ? "back to all times" : "book this time"}
          cw-id="booking-modal-book-button"
        >
          {timeTaken ? "Back" : "Book it"}
        </Button>
      </div>
    </>
  );

  const rescheduleForm = (
    <>
      <div>
        <FormInput
          label="Reason for change"
          placeholder="Reason"
          value={reason}
          onChange={onChangeReason}
          cw-id="booking-modal-reason-field"
        />
      </div>
      <div className={classes.bookItContainer}>
        {errorMsg && <span className={classes.errorMsg}>{errorMsg}</span>}
        {loading && (
          <CircularProgress size={18} color="primary" className={classes.loadingSpinner} />
        )}
        <Button
          onClick={onConfirmReschedule}
          sentiment="positive"
          disabled={loading}
          size="large"
          aria-label={timeTaken ? "back to all times" : "book this time"}
          cw-id="booking-modal-book-button"
        >
          {timeTaken ? "Back" : "Book it"}
        </Button>
      </div>
    </>
  );

  const meetingDetailsChanged =
    !!existingBooking &&
    (existingBooking.meetingName !== title || asMinutes(existingBooking) !== duration);

  return (
    <ModalDialog
      open={!!slotTimeRange}
      onClose={onClose}
      fullscreen={fullscreen}
      cw-id="link-booking-modal"
    >
      {slotTimeRange ? (
        <>
          <div className={classes.headerAndClose}>
            <h2 className={classes.header}>Book this time</h2>
            <div className="-cw-ml-3 sm:cw-ml-0 sm:-cw-mr-3">
              <Button
                variant="text"
                size="large"
                startIcon={Close}
                onClick={() => {
                  onClose();
                  setShowErrors(false);
                }}
                className={classes.close}
                cw-id="close-booking-modal"
              />
            </div>
          </div>
          <div className="cw-flex cw-flex-col cw-gap-4">
            <EventSummary
              title={title}
              duration={duration}
              prevDateTime={existingBooking?.startTime}
              dateTime={slotTimeRange.startTime.dateOrDateTime}
              attendees={
                existingBooking
                  ? [existingBooking.ownerEmail, existingBooking.attendeeEmail]
                  : undefined
              }
              compact
              timeZone={timeZone}
              variant={ConfirmationType.Book}
            />
            {meetingDetailsChanged && (
              <Notification sentiment="info">
                This booking link was edited. Some of the meeting details may have changed.
              </Notification>
            )}
          </div>
          {existingBooking ? rescheduleForm : bookForm}
        </>
      ) : (
        <></> // Empty element to avoid React error.
      )}
    </ModalDialog>
  );
};
